import { cn } from '@spiaggeit/spit-ui'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BackButton } from '@/components/BackButton'
import { SkeletonText } from '@/components/SkeletonText'
import { isKioskSelector } from '@/store/appSlice'
import {
  servicesWithCartQuantitySelector,
  ticketsInCartTotalQuantity,
} from '@/store/cartSlice/selectors/servicesWithCartQuantitySelector'
import { Cart } from '../../assets/icons/Cart'
import { ChevronUp } from '../../assets/icons/ChevronUp'
import { Info } from '../../assets/icons/Info'
import { useAppSelector } from '../../hooks/store'
import { cartSlice } from '../../store/cartSlice/index'
import { priceFormatter } from '../../utils/price'

import { SidebarServiceMobile } from './SidebarServiceMobile'
import { SubmitButton } from './SubmitButton'

interface Props {
  openFeesDialog: VoidFunction
}

export const MobileDrawer = (props: Props) => {
  const { t } = useTranslation()
  const cartTotal = useAppSelector(cartSlice.selectors.total)
  const cartFees = useAppSelector(cartSlice.selectors.quoteFees)
  const isQuotePending = useAppSelector(cartSlice.selectors.isQuotePending)
  const isKiosk = useAppSelector(isKioskSelector)

  const servicesWithCartQuantity = useAppSelector(
    servicesWithCartQuantitySelector
  )
  const totalQuantity = useAppSelector(ticketsInCartTotalQuantity)
  const [isOpen, setIsOpen] = useState(false)

  return (
    <>
      {isOpen ? (
        <div
          className="fixed left-0 top-0 z-20 h-full w-full bg-black bg-opacity-50 md:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      ) : null}

      <div
        className={cn(
          'fixed bottom-0 left-0 z-30 w-full rounded-tl-lg rounded-tr-lg bg-white shadow-[rgba(0,0,0,0.1)_0px_-4px_8px_-3px] md:hidden',
          {
            'rounded-none': isKiosk,
          }
        )}
      >
        {!isKiosk ? (
          <div
            className="flex cursor-pointer justify-center pt-2"
            onClick={() => setIsOpen((state) => !state)}
          >
            <ChevronUp className={cn('size-5', { 'rotate-180': isOpen })} />
          </div>
        ) : null}

        <div>
          <div
            className={cn({
              hidden: !isOpen,
            })}
          >
            <div className="space-y-2 divide-y divide-gray-200 px-4">
              {servicesWithCartQuantity?.map((group) => (
                <div className="py-4" key={group.serviceGroupId}>
                  <div className="mb-2 font-bold">{group.name}</div>

                  <div className="space-y-2">
                    {group.services.map((service) => (
                      <SidebarServiceMobile
                        key={service.serviceId}
                        service={service}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-gray-200 p-4 text-sm font-medium text-secondary">
              <div className="flex justify-between">
                <div>{t('tickets.summary.ticket')}</div>
                <SkeletonText
                  isPending={isQuotePending}
                  text={priceFormatter.format(cartTotal - cartFees)}
                />
              </div>

              {!isKiosk ? (
                <div className="flex justify-between">
                  <div
                    className="flex cursor-pointer space-x-1"
                    onClick={props.openFeesDialog}
                  >
                    <div className="text-sm text-secondary underline">
                      {t('common.serviceCosts')}
                    </div>{' '}
                    <Info />
                  </div>
                  <SkeletonText
                    isPending={isQuotePending}
                    text={priceFormatter.format(cartFees)}
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div
            className={cn('flex items-center gap-4 p-4', {
              'border-t border-gray-200': isOpen,
              'flex-col items-start': isKiosk,
            })}
          >
            <div className="flex items-center gap-4">
              <div className="relative">
                <button
                  className="text-black"
                  onClick={() => setIsOpen((state) => !state)}
                  type="button"
                >
                  <Cart />
                </button>

                <div className="absolute left-1/2 top-0 flex size-5 -translate-y-1/2 items-center justify-center rounded-circle bg-yellow-300 text-xs text-white">
                  {totalQuantity}
                </div>
              </div>

              <div>
                <div className="font-bold">
                  {priceFormatter.format(cartTotal)}
                </div>

                <div
                  className="flex cursor-pointer space-x-1"
                  onClick={props.openFeesDialog}
                >
                  <div className="text-sm text-secondary underline">
                    {t('tickets.summary.fees')}
                  </div>{' '}
                  <Info />
                </div>
              </div>
            </div>

            <div className="flex w-full flex-1 gap-4">
              {isKiosk ? <BackButton /> : null}

              <SubmitButton
                className={cn({ 'flex-1': isKiosk, 'ml-auto': !isKiosk })}
                isLoading={false}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
